<template>
  <div class="dashboard-body">
    <div class="row">
      <sidenav class="custom-col-width-2" />
      <div class="custom-col-width-10">
        <navbar />
        <div class="content-section">
          <section class="sub-page">
            <header class="sub-head">
              <div>
                <div class="sub-eyebrow">
                  <span class="status-dot"></span>
                  Billing &amp; plans
                </div>
                <h1>Subscription</h1>
                <p>Manage your plan, review billing history and upgrade when you grow.</p>
              </div>
              <button type="button" class="sub-refresh" :disabled="anyLoading" v-on:click="refresh">
                <i :class="['pi', anyLoading ? 'pi-spin pi-spinner' : 'pi-refresh']"></i>
                <span>Refresh</span>
              </button>
            </header>

            <!-- Loading skeleton (first load) -->
            <div v-if="subscriptionsLoading && !hasLoaded" class="sub-loading">
              <i class="pi pi-spin pi-spinner"></i>
              <span>Loading your subscription…</span>
            </div>

            <template v-else>
              <!-- Current plan hero -->
              <article class="current-card" :class="{ 'is-empty': !currentSubscription }">
                <div class="current-main">
                  <div class="current-top">
                    <span class="current-label">Current plan</span>
                    <span v-if="currentSubscription" class="badge" :class="statusFor(currentSubscription).cls">
                      {{ statusFor(currentSubscription).label }}
                    </span>
                  </div>

                  <template v-if="currentSubscription">
                    <h2 class="current-name">{{ currentPlan ? currentPlan.name : "—" }}</h2>
                    <div class="current-price">
                      <span class="amount">{{ money(currentAmount, currentCurrency) }}</span>
                      <span class="period">{{ planPeriod(currentPlan) }}</span>
                    </div>

                    <ul class="current-limits">
                      <li v-for="(limit, i) in planLimits(currentPlan)" :key="i">
                        <i class="pi pi-check-circle"></i>
                        <span>{{ limit }}</span>
                      </li>
                    </ul>
                  </template>

                  <template v-else>
                    <h2 class="current-name">No active subscription</h2>
                    <p class="current-none">Choose a plan below to unlock verifications for your team.</p>
                  </template>
                </div>

                <aside class="current-side">
                  <div v-if="currentSubscription" class="meta-row">
                    <span class="meta-key">{{ currentPlan && currentPlan.isFree ? "Trial ends" : "Renews / expires" }}</span>
                    <span class="meta-val">{{ $filters.getDateOnly(currentSubscription.endDate) }}</span>
                  </div>
                  <div v-if="currentSubscription" class="meta-row">
                    <span class="meta-key">Days remaining</span>
                    <span class="meta-val">{{ daysRemaining(currentSubscription.endDate) }}</span>
                  </div>
                  <div v-if="currentSubscription && currentSubscription.invoiceId" class="meta-row">
                    <span class="meta-key">Invoice</span>
                    <span class="meta-val">#{{ currentSubscription.invoiceId }}</span>
                  </div>
                  <button type="button" class="current-cta" v-on:click="scrollToPlans">
                    <i class="pi pi-arrow-up-right"></i>
                    {{ currentPlan && currentPlan.isFree ? "Upgrade now" : "Change plan" }}
                  </button>
                </aside>
              </article>

              <!-- Pending notice -->
              <div v-if="pendingSubscription" class="pending-note">
                <i class="pi pi-clock"></i>
                <span>
                  <strong>{{ pendingSubscription.subscriptionPlan ? pendingSubscription.subscriptionPlan.name : "A plan" }}</strong>
                  is queued and starts on {{ $filters.getDateOnly(pendingSubscription.startDate) }}.
                </span>
              </div>

              <!-- Available plans -->
              <div ref="plansSection" class="plans-section">
                <div class="section-head">
                  <h3>Choose your plan</h3>
                  <p v-if="!canManage" class="section-note">Only owners and admins can change the plan.</p>
                  <p v-else-if="pendingSubscription" class="section-note">A plan change is already queued — no further upgrades until it starts.</p>
                </div>

                <div v-if="plansLoading" class="sub-loading compact">
                  <i class="pi pi-spin pi-spinner"></i>
                  <span>Loading plans…</span>
                </div>

                <div v-else-if="!sortedPlans.length" class="plans-empty">
                  <i class="pi pi-inbox"></i>
                  <span>No plans are available right now.</span>
                </div>

                <div v-else class="plan-grid">
                  <article
                    v-for="plan in sortedPlans"
                    :key="plan.id"
                    class="plan-card"
                    :class="{ 'is-current': isCurrentPlan(plan), 'is-recommended': isRecommended(plan) }"
                  >
                    <span v-if="isRecommended(plan)" class="plan-ribbon">Most popular</span>
                    <div class="plan-card-head">
                      <h4>{{ plan.name }}</h4>
                      <div class="plan-price">
                        <span class="amount">{{ money(plan.totalPrice, defaultCurrency) }}</span>
                        <span class="period">{{ planPeriod(plan) }}</span>
                      </div>
                    </div>

                    <ul class="plan-features">
                      <li v-for="(feat, i) in planFeatures(plan)" :key="i">
                        <i class="pi pi-check"></i>
                        <span>{{ feat }}</span>
                      </li>
                    </ul>

                    <button
                      type="button"
                      class="plan-btn"
                      :class="planBtn(plan).cls"
                      :disabled="planBtn(plan).disabled"
                      v-on:click="upgrade(plan)"
                    >
                      <span v-if="initializingPlanId === plan.id" class="plan-spinner" aria-hidden="true"></span>
                      <span>{{ planBtn(plan).label }}</span>
                    </button>
                  </article>
                </div>
              </div>

              <!-- History -->
              <div class="history-section">
                <div class="section-head">
                  <h3>Billing history</h3>
                  <p class="section-note">{{ historyCountLabel }}</p>
                </div>

                <div class="list-page__table">
                  <tableLoader v-if="subscriptionsLoading" message="Loading history…" />
                  <div v-if="!subscriptionsLoading" class="table-scroller">
                    <table>
                      <thead>
                        <tr>
                          <th scope="col">Invoice</th>
                          <th scope="col">Plan</th>
                          <th scope="col">Amount</th>
                          <th scope="col">Period</th>
                          <th scope="col">Payment</th>
                          <th scope="col">Status</th>
                          <th scope="col">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(sub, index) in subscriptions" v-bind:key="sub.id || index">
                          <th scope="row">{{ sub.invoiceId ? `#${sub.invoiceId}` : "—" }}</th>
                          <td class="row-name">{{ sub.subscriptionPlan ? sub.subscriptionPlan.name : "—" }}</td>
                          <td>{{ money(rowAmount(sub), rowCurrency(sub)) }}</td>
                          <td>
                            <span class="period-cell">
                              {{ $filters.getDateOnly(sub.startDate) }} – {{ $filters.getDateOnly(sub.endDate) }}
                            </span>
                          </td>
                          <td>{{ rowPaymentMethod(sub) }}</td>
                          <td><span class="badge" :class="statusFor(sub).cls">{{ statusFor(sub).label }}</span></td>
                          <td>{{ $filters.getDateOnly(sub.createdAt) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div
                    v-if="!subscriptionsLoading && (!subscriptions || subscriptions.length === 0)"
                    class="list-page__empty"
                  >
                    <i class="pi pi-receipt"></i>
                    <div>No billing history yet.</div>
                  </div>
                </div>

                <Pagination
                  :metaData="subscriptionsMeta"
                  v-model:currentPage="currentPage"
                  v-model:perPage="perPage"
                  v-on:change="onPaginationChange"
                />
              </div>
            </template>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import sidenav from "@/components/Sidenav.vue";
import navbar from "@/components/Navbar.vue";
import Pagination from "@/components/Pagination.vue";
import tableLoader from "@/components/Table_Loader.vue";
import {
  GET_SUBSCRIPTIONS,
  GET_SUBSCRIPTION_PLANS,
  INITIALIZE_SUBSCRIPTION,
} from "@/store/action_types";
import { USER_ROLES } from "@/utils/enums";
import { TOAST_GLOBAL_CONFIG } from "@/utils/const";

const MANAGER_ROLES = [USER_ROLES.owner, USER_ROLES.admin, USER_ROLES.superAdmin];

export default {
  title: "Subscription",
  components: {
    sidenav,
    navbar,
    Pagination,
    tableLoader,
  },
  data() {
    return {
      defaultCurrency: "ETB",
      currentPage: 1,
      perPage: 10,
      hasLoaded: false,
      initializingPlanId: null,
    };
  },
  computed: {
    account() {
      return this.$store.getters.account.data;
    },
    canManage() {
      return !!(this.account && MANAGER_ROLES.includes(this.account.role));
    },
    subscriptions() {
      return this.$store.getters.subscriptions.data || [];
    },
    subscriptionsLoading() {
      return this.$store.getters.subscriptions.isLoading;
    },
    subscriptionsMeta() {
      return this.$store.getters.subscriptions.metaData;
    },
    plans() {
      return this.$store.getters.subscriptionPlans.data || [];
    },
    plansLoading() {
      return this.$store.getters.subscriptionPlans.isLoading;
    },
    anyLoading() {
      return this.subscriptionsLoading || this.plansLoading;
    },
    // The single active, non-pending subscription (the free trial counts).
    currentSubscription() {
      return this.subscriptions.find((s) => s.isActive && !s.isPending) || null;
    },
    pendingSubscription() {
      return this.subscriptions.find((s) => s.isPending) || null;
    },
    currentPlan() {
      return this.currentSubscription ? this.currentSubscription.subscriptionPlan : null;
    },
    currentAmount() {
      return this.rowAmount(this.currentSubscription);
    },
    currentCurrency() {
      return this.rowCurrency(this.currentSubscription);
    },
    sortedPlans() {
      return [...this.plans].sort(
        (a, b) => Number(a.totalPrice) - Number(b.totalPrice),
      );
    },
    historyCountLabel() {
      const total = this.subscriptionsMeta?.totalRecords ?? this.subscriptions.length;
      return total === 1 ? "1 record" : `${total} records`;
    },
  },
  methods: {
    money(value, currency) {
      const num = Number(value);
      if (value == null || isNaN(num)) return "—";
      return this.$filters.formatPrice(num, currency || this.defaultCurrency);
    },
    planPeriod(plan) {
      if (!plan) return "";
      const d = Number(plan.duration);
      if (plan.isFree) return "free";
      if (d >= 360) return "/ year";
      if (d >= 28 && d <= 31) return "/ month";
      return `/ ${d} days`;
    },
    planLimits(plan) {
      if (!plan) return [];
      const out = [];
      if (plan.verificationLimit == null) {
        out.push("Unlimited verifications");
      } else {
        const period = plan.limitPeriod === "total" ? "lifetime" : plan.limitPeriod;
        out.push(`${plan.verificationLimit} ${period} verifications`);
      }
      out.push(plan.maxUsers == null ? "Unlimited team members" : `${plan.maxUsers} team member${plan.maxUsers === 1 ? "" : "s"}`);
      out.push(plan.maxDevices == null ? "Unlimited devices" : `${plan.maxDevices} device${plan.maxDevices === 1 ? "" : "s"}`);
      return out;
    },
    // Prefer the plan's advertised feature bullets; fall back to its limits.
    planFeatures(plan) {
      if (plan && Array.isArray(plan.features) && plan.features.length) {
        return plan.features;
      }
      return this.planLimits(plan);
    },
    rowAmount(sub) {
      if (!sub) return null;
      if (sub.payment && sub.payment.amount != null) return sub.payment.amount;
      if (sub.subscriptionPlan && sub.subscriptionPlan.totalPrice != null) return sub.subscriptionPlan.totalPrice;
      return null;
    },
    rowCurrency(sub) {
      if (sub && sub.payment && sub.payment.currency) return sub.payment.currency;
      return this.defaultCurrency;
    },
    rowPaymentMethod(sub) {
      if (sub && sub.payment && sub.payment.paymentMethod && sub.payment.paymentMethod.name) {
        return sub.payment.paymentMethod.name;
      }
      if (sub && sub.subscriptionPlan && sub.subscriptionPlan.isFree) return "Free trial";
      return "—";
    },
    statusFor(sub) {
      if (!sub) return { label: "—", cls: "badge-secondary" };
      if (sub.isPending) return { label: "Pending", cls: "badge-warning" };
      const ended = sub.endDate && new Date(sub.endDate).getTime() < Date.now();
      if (sub.isActive && !ended) return { label: "Active", cls: "badge-success" };
      if (ended) return { label: "Expired", cls: "badge-secondary" };
      return { label: "Inactive", cls: "badge-secondary" };
    },
    daysRemaining(endDate) {
      if (!endDate) return "—";
      const diff = new Date(endDate).getTime() - Date.now();
      if (diff <= 0) return "0 days";
      const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
      return days === 1 ? "1 day" : `${days} days`;
    },
    isCurrentPlan(plan) {
      return !!(this.currentPlan && this.currentPlan.id === plan.id);
    },
    // Highlight the cheapest paid upgrade above the current plan as "most popular".
    isRecommended(plan) {
      if (this.isCurrentPlan(plan)) return false;
      const upgrades = this.sortedPlans.filter((p) => !this.isCurrentPlan(p) && this.isUpgrade(p));
      return upgrades.length ? upgrades[0].id === plan.id : false;
    },
    isUpgrade(plan) {
      if (!this.currentPlan) return true;
      return Number(plan.totalPrice) > Number(this.currentPlan.totalPrice);
    },
    planBtn(plan) {
      if (this.isCurrentPlan(plan)) {
        return { label: "Current plan", cls: "is-current-btn", disabled: true };
      }
      const blocked = !this.canManage || !!this.pendingSubscription || this.initializingPlanId != null;
      const label = this.isUpgrade(plan) ? "Upgrade" : "Switch to this";
      return { label, cls: "is-primary-btn", disabled: blocked };
    },
    scrollToPlans() {
      const el = this.$refs.plansSection;
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    upgrade(plan) {
      if (this.isCurrentPlan(plan) || this.initializingPlanId) return;
      if (!this.canManage) {
        this.$toast.error("Only owners and admins can change the plan.", TOAST_GLOBAL_CONFIG);
        return;
      }
      this.initializingPlanId = plan.id;
      this.$store
        .dispatch(INITIALIZE_SUBSCRIPTION, { subscriptionPlanId: plan.id })
        .then((res) => {
          if (res && res.checkoutUrl) {
            // Hand off to Chapa's hosted checkout.
            window.location.href = res.checkoutUrl;
            return;
          }
          this.initializingPlanId = null;
          this.$toast.error(
            this.$store.getters.errorMsg || "Could not start checkout. Please try again.",
            TOAST_GLOBAL_CONFIG,
          );
        })
        .catch(() => {
          this.initializingPlanId = null;
          this.$toast.error("Could not start checkout. Please try again.", TOAST_GLOBAL_CONFIG);
        });
    },
    onPaginationChange({ page, perPage }) {
      this.currentPage = page;
      this.perPage = perPage;
      this.getSubscriptions();
    },
    getSubscriptions() {
      this.$store
        .dispatch(GET_SUBSCRIPTIONS, { page: this.currentPage, limit: this.perPage })
        .then(() => {
          this.hasLoaded = true;
        });
    },
    getPlans() {
      this.$store.dispatch(GET_SUBSCRIPTION_PLANS, {
        isActive: true,
        isUserSelectable: true,
        limit: 100,
      });
    },
    refresh() {
      this.getSubscriptions();
      this.getPlans();
    },
  },
  mounted() {
    this.getSubscriptions();
    this.getPlans();
  },
};
</script>
<style scoped>
.sub-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding-bottom: 36px;
}

/* Header */
.sub-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
}

.sub-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  width: fit-content;
  padding: 6px 12px;
  margin-bottom: 10px;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-pill);
  color: var(--ehs-color-primary-active);
  background: var(--ehs-color-primary-soft);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ehs-color-success);
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.12);
}

.sub-head h1 {
  margin: 0;
  color: var(--ehs-color-ink);
  font-family: var(--ehs-font-display);
  font-size: 1.7rem;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.sub-head p {
  margin: 8px 0 0;
  max-width: 560px;
  color: var(--ehs-color-muted);
  font-size: 0.9rem;
  line-height: 1.55;
}

.sub-refresh {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 15px;
  border: 1px solid var(--ehs-color-border);
  background: var(--ehs-color-surface);
  border-radius: var(--ehs-radius-pill);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ehs-color-ink);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.sub-refresh:hover:not(:disabled) {
  background: var(--ehs-color-primary-soft);
  border-color: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary-hover);
}

.sub-refresh:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Loading */
.sub-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 70px 24px;
  background: var(--ehs-color-surface);
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-lg);
  color: var(--ehs-color-muted);
  font-size: 0.9rem;
}

.sub-loading.compact {
  padding: 36px 24px;
}

.sub-loading .pi {
  font-size: 1.6rem;
  color: var(--ehs-color-primary);
}

/* Current plan hero */
.current-card {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 0;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-xl);
  overflow: hidden;
  background:
    radial-gradient(120% 140% at 0% 0%, rgba(115, 196, 29, 0.10) 0%, rgba(115, 196, 29, 0) 55%),
    var(--ehs-color-surface);
  box-shadow: var(--ehs-shadow-soft);
}

.current-main {
  padding: 26px 28px;
}

.current-top {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.current-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ehs-color-muted);
}

.current-name {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.9rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--ehs-color-ink);
  text-transform: capitalize;
}

.current-none {
  margin: 8px 0 0;
  color: var(--ehs-color-muted);
  font-size: 0.9rem;
}

.current-price {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 6px;
}

.current-price .amount {
  font-family: var(--ehs-font-display);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--ehs-color-primary-active);
}

.current-price .period {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--ehs-color-muted);
}

.current-limits {
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
  display: grid;
  gap: 8px;
}

.current-limits li {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 0.85rem;
  color: var(--ehs-color-text);
}

.current-limits .pi {
  color: var(--ehs-color-success);
  font-size: 0.85rem;
}

.current-side {
  padding: 26px 28px;
  border-left: 1px solid var(--ehs-color-border);
  background: var(--ehs-color-canvas);
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.meta-key {
  font-size: 0.78rem;
  color: var(--ehs-color-muted);
  font-weight: 500;
}

.meta-val {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--ehs-color-ink);
}

.current-cta {
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 16px;
  border: 1px solid var(--ehs-color-primary);
  border-radius: var(--ehs-radius-md);
  background: var(--ehs-color-primary);
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.current-cta:hover {
  background: var(--ehs-color-primary-hover);
  border-color: var(--ehs-color-primary-hover);
}

.current-cta .pi {
  font-size: 0.75rem;
}

/* Pending */
.pending-note {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border: 1px solid var(--ehs-color-warning-soft);
  background: var(--ehs-color-warning-soft);
  border-radius: var(--ehs-radius-md);
  color: #92400e;
  font-size: 0.85rem;
}

.pending-note .pi {
  font-size: 0.95rem;
}

/* Section heads */
.section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-head h3 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ehs-color-ink);
}

.section-note {
  margin: 0;
  font-size: 0.78rem;
  color: var(--ehs-color-muted);
}

.plans-section {
  margin-top: 4px;
}

/* Plan grid */
.plan-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(255px, 1fr));
  gap: 16px;
}

.plan-card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 22px 22px 20px;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-lg);
  background: var(--ehs-color-surface);
  box-shadow: var(--ehs-shadow-card);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.plan-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--ehs-shadow-soft);
}

.plan-card.is-current {
  border-color: var(--ehs-color-primary);
  box-shadow: 0 0 0 1px var(--ehs-color-primary), var(--ehs-shadow-card);
}

.plan-card.is-recommended {
  border-color: var(--ehs-color-primary-active);
}

.plan-ribbon {
  position: absolute;
  top: 14px;
  right: 14px;
  padding: 3px 9px;
  border-radius: var(--ehs-radius-pill);
  background: var(--ehs-color-primary);
  color: #ffffff;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.plan-card-head h4 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ehs-color-ink);
  text-transform: capitalize;
}

.plan-price {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 6px;
}

.plan-price .amount {
  font-family: var(--ehs-font-display);
  font-size: 1.55rem;
  font-weight: 700;
  color: var(--ehs-color-ink);
}

.plan-price .period {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ehs-color-muted);
}

.plan-features {
  list-style: none;
  margin: 16px 0 18px;
  padding: 16px 0 0;
  border-top: 1px solid var(--ehs-color-border);
  display: grid;
  gap: 9px;
  flex: 1;
}

.plan-features li {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 0.82rem;
  color: var(--ehs-color-text);
  line-height: 1.4;
}

.plan-features .pi {
  margin-top: 2px;
  font-size: 0.72rem;
  color: var(--ehs-color-primary);
}

.plan-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 11px 16px;
  border-radius: var(--ehs-radius-md);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.plan-btn.is-primary-btn {
  background: var(--ehs-color-primary);
  border-color: var(--ehs-color-primary);
  color: #ffffff;
}

.plan-btn.is-primary-btn:hover:not(:disabled) {
  background: var(--ehs-color-primary-hover);
  border-color: var(--ehs-color-primary-hover);
}

.plan-btn.is-current-btn {
  background: var(--ehs-color-primary-soft);
  border-color: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary-active);
}

.plan-btn:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.plan-spinner {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #ffffff;
  animation: plan-spin 0.6s linear infinite;
}

@keyframes plan-spin {
  to { transform: rotate(360deg); }
}

.plans-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 48px 24px;
  border: 1px dashed var(--ehs-color-border-strong);
  border-radius: var(--ehs-radius-lg);
  color: var(--ehs-color-muted);
  font-size: 0.88rem;
}

.plans-empty .pi {
  font-size: 1.6rem;
  color: var(--ehs-color-soft);
}

/* History */
.history-section {
  margin-top: 6px;
}

.period-cell {
  font-size: 0.8rem;
  color: var(--ehs-color-text);
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  .sub-head {
    flex-direction: column;
    align-items: stretch;
  }

  .sub-head h1 {
    font-size: 1.4rem;
  }

  .current-card {
    grid-template-columns: 1fr;
  }

  .current-side {
    border-left: 0;
    border-top: 1px solid var(--ehs-color-border);
  }

  .plan-grid {
    grid-template-columns: 1fr;
  }
}
</style>
